import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, getApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtLogDay, orgToday } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import DocumentDetailsDialog from "@/components/DocumentDetailsDialog";
import {
  Folder, FolderOpen, FolderPlus, ChevronRight, ChevronDown, Pencil, Trash2, Loader2, Home, Search, FilePlus2, FileText,
} from "lucide-react";
import { DOCS_ACCENT as ACCENT, LIBRARY_META as META, ROOT, indexFolders, folderPath as pathOf, fmtDay, suggestPrefix } from "@/lib/govDocs";

export function DocStatusBadges({ d }) {
  const pill = "inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap";
  return (
    <span className="inline-flex flex-wrap gap-1">
      {d.active_version ? <span className={`${pill} bg-emerald-50 text-emerald-700 border-emerald-200`}>Active · v{d.active_version}</span>
        : d.retired ? <span className={`${pill} bg-slate-100 text-slate-500 border-slate-200`}>Archived</span> : null}
      {d.has_draft && (d.awaiting_approval
        ? <span className={`${pill} bg-violet-50 text-violet-700 border-violet-200`}>Awaiting approval</span>
        : <span className={`${pill} bg-amber-50 text-amber-700 border-amber-200`}>{d.active_version || d.retired ? "Draft in progress" : "Draft"}</span>)}
    </span>
  );
}

export default function DocumentLibrary({ library }) {
  const meta = META[library];
  const { can } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const current = params.get("folder") || null;      // selected folder id (null = library root)
  const setCurrent = useCallback((id) => setParams(id ? { folder: id } : {}, { replace: true }), [setParams]);
  const [folders, setFolders] = useState(null);
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [newDoc, setNewDoc] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState(null);        // { mode: "create"|"edit", folder?, name, parent }
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.govFolders(library).then((r) => setFolders(r.folders))
      .catch((e) => { setFolders([]); toast.error(getApiError(e, "Couldn't load folders.")); });
    api.govDocuments(library).then((r) => setDocs(r.documents))
      .catch((e) => { setDocs([]); toast.error(getApiError(e, "Couldn't load documents.")); });
  }, [library]);

  useEffect(() => { setFolders(null); setDocs(null); setExpanded({}); load(); }, [load]);

  const { byId, kids } = useMemo(() => indexFolders(folders || []), [folders]);
  const crumbs = pathOf(byId, current);
  const children = kids[current || ROOT] || [];

  // A folder deleted elsewhere: fall back to the root. Otherwise make sure its path is expanded.
  useEffect(() => {
    if (!current || !folders) return;
    if (!byId[current]) { setCurrent(null); return; }
    setExpanded((x) => { const n = { ...x }; pathOf(byId, current).forEach((f) => { n[f.id] = true; }); return n; });
  }, [current, folders, byId, setCurrent]);

  const today = orgToday();
  const visibleDocs = useMemo(() => (docs || []).filter((d) => showArchived || d.active_version || d.has_draft), [docs, showArchived]);
  const docCount = useMemo(() => {
    const m = {};
    visibleDocs.forEach((d) => { const k = d.folder_id || ROOT; m[k] = (m[k] || 0) + 1; });
    return m;
  }, [visibleDocs]);
  const term = q.trim().toLowerCase();
  // Searching looks across the whole library; otherwise show the current folder.
  const listed = useMemo(() => visibleDocs.filter((d) => (term
    ? `${d.code || ""} ${d.title}`.toLowerCase().includes(term)
    : (d.folder_id || null) === current)), [visibleDocs, term, current]);

  const open = (id) => { setQ(""); setCurrent(id); };

  // Destinations for a folder being moved: anything except itself and its descendants.
  const destinations = useMemo(() => {
    const excluded = new Set();
    if (dialog?.mode === "edit") {
      const walk = (id) => { excluded.add(id); (kids[id] || []).forEach((k) => walk(k.id)); };
      walk(dialog.folder.id);
    }
    return (folders || []).filter((f) => !excluded.has(f.id))
      .map((f) => ({ id: f.id, label: pathOf(byId, f.id).map((p) => p.name).join(" / ") }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dialog, folders, byId, kids]);

  const save = async () => {
    const payload = { name: dialog.name, parent_id: dialog.parent === ROOT ? null : dialog.parent,
      code_prefix: dialog.parent === ROOT ? dialog.prefix || null : null };
    setSaving(true);
    try {
      if (dialog.mode === "create") {
        const f = await api.govCreateFolder(library, payload);
        toast.success(`Folder "${f.name}" created`);
      } else {
        await api.govUpdateFolder(library, dialog.folder.id, payload);
        toast.success("Folder updated");
      }
      setDialog(null);
      load();
    } catch (e) { toast.error(getApiError(e, "Couldn't save the folder.")); }
    finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete the folder "${f.name}"? It must be empty.`)) return;
    try {
      await api.govDeleteFolder(library, f.id);
      toast.success("Folder deleted");
      if (current === f.id) setCurrent(f.parent_id || null);
      load();
    } catch (e) { toast.error(getApiError(e, "Couldn't delete the folder.")); }
  };

  // Plain recursive function (not a self-rendering component) — see CLAUDE.md.
  const renderTree = (parentKey, depth) => (kids[parentKey] || []).map((f) => {
    const hasKids = !!(kids[f.id] || []).length;
    const isOpen = !!expanded[f.id];
    const active = current === f.id;
    return (
      <div key={f.id}>
        <div className={`flex items-center gap-1 rounded-md pr-2 text-sm cursor-pointer ${active ? "bg-[#6b4f8a14] text-[#6b4f8a] font-medium" : "text-slate-600 hover:bg-slate-50"}`}
          style={{ paddingLeft: 4 + depth * 14 }} data-testid={`folder-tree-${f.id}`}>
          <button className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-0" disabled={!hasKids}
            onClick={() => setExpanded((x) => ({ ...x, [f.id]: !isOpen }))} aria-label={isOpen ? "Collapse" : "Expand"}>
            {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          </button>
          <button className="flex items-center gap-1.5 py-1.5 min-w-0 flex-1 text-left" onClick={() => open(f.id)}>
            {active ? <FolderOpen className="h-4 w-4 shrink-0" /> : <Folder className="h-4 w-4 shrink-0 text-slate-400" />}
            <span className="truncate">{f.name}</span>
          </button>
        </div>
        {hasKids && isOpen && renderTree(f.id, depth + 1)}
      </div>
    );
  });

  const Icon = meta.icon;
  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid={`doc-library-${library}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <Icon className="h-5 w-5" style={{ color: ACCENT }} />
            <h1 className="text-xl font-semibold font-brand text-slate-800">{meta.label}</h1>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">{meta.desc}</p>
        </div>
        {can.management && (
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" className="h-9 text-xs" data-testid="folder-new"
              onClick={() => setDialog({ mode: "create", name: "", parent: current || ROOT, prefix: "", prefixTouched: false })}>
              <FolderPlus className="h-4 w-4 mr-1" /> New folder
            </Button>
            <Button size="sm" className="h-9 text-xs" style={{ backgroundColor: ACCENT }} data-testid="doc-new" onClick={() => setNewDoc(true)}>
              <FilePlus2 className="h-4 w-4 mr-1" /> New {meta.singular}
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search all ${meta.label.toLowerCase()}…`} className="h-9 pl-9 text-sm bg-white" data-testid="doc-search" />
        </div>
        {can.management && (
          <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="doc-show-archived" /> Show archived
          </label>
        )}
      </div>

      {folders === null || docs === null ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid md:grid-cols-[240px_1fr] gap-4 mt-4">
          <div className="bg-white border border-slate-200 rounded-xl p-2 h-fit md:sticky md:top-4" data-testid="folder-tree">
            <button onClick={() => open(null)}
              className={`w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm ${current === null ? "bg-[#6b4f8a14] text-[#6b4f8a] font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
              <Home className="h-4 w-4" /> All folders
            </button>
            {renderTree(ROOT, 0)}
            {!folders.length && <div className="text-[11px] text-slate-400 px-2 py-3">No folders yet.</div>}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
              {term ? (
                <div className="text-sm text-slate-600">{listed.length} result{listed.length === 1 ? "" : "s"} for “{q.trim()}”</div>
              ) : (
              <nav className="flex items-center gap-1 text-sm min-w-0 flex-wrap" data-testid="folder-breadcrumb">
                <button className="text-slate-500 hover:text-[#6b4f8a]" onClick={() => open(null)}>{meta.label}</button>
                {crumbs.map((c) => (
                  <span key={c.id} className="flex items-center gap-1 min-w-0">
                    <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                    <button className={`truncate ${c.id === current ? "font-semibold text-slate-800" : "text-slate-500 hover:text-[#6b4f8a]"}`} onClick={() => open(c.id)}>{c.name}</button>
                  </span>
                ))}
              </nav>
              )}
              {can.management && current && !term && (
                <div className="flex items-center gap-1 shrink-0">
                  <button className="text-slate-400 hover:text-[#6b4f8a] p-1.5 rounded-md hover:bg-slate-100" title="Rename or move" data-testid="folder-edit"
                    onClick={() => setDialog({ mode: "edit", folder: byId[current], name: byId[current].name, parent: byId[current].parent_id || ROOT,
                      prefix: byId[current].code_prefix || "", prefixTouched: true })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button className="text-slate-400 hover:text-rose-600 p-1.5 rounded-md hover:bg-rose-50" title="Delete folder" data-testid="folder-delete"
                    onClick={() => remove(byId[current])}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            {!term && children.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 p-4 border-b border-slate-100">
                {children.map((f) => (
                  <button key={f.id} onClick={() => open(f.id)} data-testid={`folder-card-${f.id}`}
                    className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:border-[#6b4f8a] hover:bg-slate-50 transition-colors">
                    <Folder className="h-5 w-5 shrink-0" style={{ color: ACCENT }} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-700 truncate">{f.name}</div>
                      <div className="text-[11px] text-slate-400">
                        {[(kids[f.id] || []).length && `${(kids[f.id] || []).length} folder${(kids[f.id] || []).length === 1 ? "" : "s"}`,
                          `${docCount[f.id] || 0} document${docCount[f.id] === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {!listed.length ? (
              <div className="text-center py-14 text-sm text-slate-400">
                {term ? "No documents match your search." : `No documents ${current ? "in this folder" : "at the top level"} yet.`}
              </div>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="doc-list">
                {listed.map((d) => {
                  const overdue = d.active_version && d.review_due && d.review_due < today;
                  return (
                    <button key={d.id} onClick={() => nav(`/documents/${library}/${d.id}`)} data-testid={`doc-row-${d.id}`}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50/70 transition-colors">
                      <FileText className="h-4 w-4 mt-0.5 shrink-0" style={{ color: ACCENT }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800">
                          {d.code && <span className="text-slate-400 font-normal mr-1.5">{d.code}</span>}{d.title}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                          {term && d.folder_id && byId[d.folder_id] && <span>{pathOf(byId, d.folder_id).map((f) => f.name).join(" / ")}</span>}
                          {d.published_at && d.active_version && <span>Effective {fmtLogDay(d.published_at)}</span>}
                          {d.active_version && d.review_due && (
                            <span className={overdue ? "text-rose-600 font-medium" : ""}>{overdue ? "Review overdue" : "Review due"} {fmtDay(d.review_due)}</span>
                          )}
                          {d.owner_name && <span>Owner: {d.owner_name}</span>}
                        </div>
                      </div>
                      {can.management && <DocStatusBadges d={d} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <DocumentDetailsDialog open={newDoc} library={library} doc={null} folders={folders || []} defaultFolder={current || ROOT}
        onClose={() => setNewDoc(false)} onSaved={(d) => { setNewDoc(false); nav(`/documents/${library}/${d.id}`); }} />

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-base flex items-center gap-2">
              <FolderPlus className="h-4 w-4" style={{ color: ACCENT }} />
              {dialog?.mode === "create" ? "New folder" : "Rename or move folder"}
            </DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-3 pt-1">
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Folder name</Label>
                <Input autoFocus value={dialog.name} maxLength={80} className="h-9 text-sm" data-testid="folder-name"
                  onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value,
                    prefix: d.prefixTouched ? d.prefix : suggestPrefix(e.target.value) }))}
                  onKeyDown={(e) => { if (e.key === "Enter" && dialog.name.trim()) save(); }} />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Inside</Label>
                <Select value={dialog.parent} onValueChange={(v) => setDialog((d) => ({ ...d, parent: v }))}>
                  <SelectTrigger className="h-9 text-sm bg-white" data-testid="folder-parent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ROOT} className="text-sm">{meta.label} (top level)</SelectItem>
                    {destinations.map((d) => <SelectItem key={d.id} value={d.id} className="text-sm">{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {dialog.parent === ROOT && (
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Code prefix (optional)</Label>
                  <Input value={dialog.prefix} maxLength={10} placeholder="e.g. GOV" className="h-9 text-sm uppercase w-36" data-testid="folder-prefix"
                    onChange={(e) => setDialog((d) => ({ ...d, prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""), prefixTouched: true }))} />
                  <div className="text-[11px] text-slate-400">
                    New documents in this folder (and its sub-folders) get a suggested code like {(dialog.prefix || "GOV")}-001.
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="outline" size="sm" onClick={() => setDialog(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: ACCENT }} onClick={save} disabled={saving || !dialog?.name?.trim()} data-testid="folder-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{dialog?.mode === "create" ? "Create folder" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
